import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, PlayCircle, Crown, Check, Sparkles } from "lucide-react";
import { formatInr } from "@/lib/cart";
import { PRICING, usePricing } from "@/lib/pricing";

const buildPkgPreview = () => [
  {
    id: "elite",
    name: PRICING.tiers.elite.name,
    price: PRICING.tiers.elite.price,
    note: "Any 2 courses",
    badge: null,
    features: ["WhatsApp Community", "Student Support", "Course Updates"],
  },
  {
    id: "basic",
    name: PRICING.tiers.basic.name,
    price: PRICING.tiers.basic.price,
    note: "3 courses + 2 free",
    badge: "Most Popular",
    features: ["Personal Mentorship", "Guidance Sessions", "Skill Development"],
  },
  {
    id: "premium",
    name: PRICING.tiers.premium.name,
    price: PRICING.tiers.premium.price,
    note: `All ${PRICING.totalCourses} courses`,
    badge: "Recommended",
    features: ["1% Community Club", "Live Q&A Sessions", "Career Growth"],
  },
];

function PackagesPreview() {
  return (
    <div
      data-testid="hero-packages-preview"
      className="relative rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white/80 dark:bg-[#0F172A]/60 glass hairline overflow-hidden"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/80 dark:border-white/10">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]">
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <div className="leading-none">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Choose Your Path
            </div>
            <div className="mt-1 font-display text-[15px] font-semibold tracking-tight text-[#0F172A] dark:text-white">
              3 access levels. Start in 2 minutes.
            </div>
          </div>
        </div>
        <a
          href="#packages"
          className="hidden sm:inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#2563EB] dark:text-[#06B6D4]"
        >
          Compare <ArrowUpRight className="h-3 w-3" />
        </a>
      </div>

      <div className="p-4 sm:p-5 space-y-3">
        {buildPkgPreview().map((p) => {
          const isPremium = p.id === "premium";
          const isPopular = p.id === "basic";
          return (
            <a
              key={p.id}
              href="#packages"
              data-testid={`hero-package-${p.id}`}
              className={`group relative block rounded-2xl border overflow-hidden p-4 transition-all hover:-translate-y-0.5 ${
                isPremium
                  ? "border-[#06B6D4]/40 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] text-white"
                  : isPopular
                    ? "border-[#2563EB]/40 ring-1 ring-[#2563EB]/20 bg-white dark:bg-white/[0.04]"
                    : "border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04]"
              }`}
            >
              {isPremium && (
                <div className="absolute inset-0 gradient-mesh opacity-30 pointer-events-none" />
              )}
              <div className="relative flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {isPremium && <Crown className="h-3.5 w-3.5 text-[#06B6D4]" />}
                    <span
                      className={`font-display text-[15px] font-semibold tracking-tight truncate ${
                        isPremium ? "text-white" : "text-[#0F172A] dark:text-white"
                      }`}
                    >
                      {p.name}
                    </span>
                    {p.badge && (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] font-semibold ${
                          isPremium
                            ? "bg-[#06B6D4] text-[#0F172A]"
                            : "bg-[#2563EB] text-white"
                        }`}
                      >
                        {p.badge}
                      </span>
                    )}
                  </div>
                  <div
                    className={`mt-1.5 text-[11px] ${
                      isPremium ? "text-slate-300" : "text-slate-500 dark:text-slate-400"
                    }`}
                  >
                    {p.note} ·{" "}
                    {p.features.slice(0, 2).join(" · ")}
                  </div>
                </div>
                <div className="text-right flex-none">
                  <div
                    className={`font-display text-xl font-semibold tracking-tighter ${
                      isPremium ? "text-white" : "text-[#0F172A] dark:text-white"
                    }`}
                  >
                    {formatInr(p.price)}
                  </div>
                  <div
                    className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${
                      isPremium ? "text-[#06B6D4]" : "text-[#2563EB] dark:text-[#06B6D4]"
                    }`}
                  >
                    Choose <ArrowRight className="h-2.5 w-2.5" />
                  </div>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] px-4 py-3">
          <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
            One-time payment · Lifetime access · Secure via Razorpay
          </div>
          <a
            href="#packages"
            data-testid="hero-packages-cta"
            className="inline-flex items-center gap-1 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-3 py-1.5 text-xs font-medium"
          >
            View all <ArrowRight className="h-3 w-3" />
          </a>
        </div>
      </div>

      <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-[#06B6D4]/30 blur-3xl pointer-events-none" />
      <div className="absolute -top-12 -left-12 h-40 w-40 rounded-full bg-[#2563EB]/25 blur-3xl pointer-events-none" />
    </div>
  );
}


export default function Hero() {
  usePricing();
  return (
    <section
      id="top"
      data-testid="section-hero"
      className="relative pt-24 pb-12 sm:pt-32 sm:pb-20 gradient-mesh overflow-x-clip"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 min-w-0">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200/80 dark:border-white/10 bg-white/70 dark:bg-white/5 glass px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-slate-700 dark:text-slate-200"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#06B6D4]" />
              The Skillpreneur Academy
            </motion.div>

            <motion.h1
              data-testid="hero-headline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-[2.5rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl mt-6 font-semibold tracking-tighter text-[#0F172A] dark:text-white text-balance break-words"
            >
              Learn High-Income Skills.
              <span className="block">
                Build Your{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Future.</span>
                  <span className="absolute inset-x-0 bottom-1.5 h-3 bg-[#06B6D4]/40 -z-0 rounded" />
                </span>
              </span>
            </motion.h1>

            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-xl"
            >
              Master practical skills that help you create opportunities, build
              expertise, and grow in the digital economy. Choose a package,
              pick your courses, and learn alongside a community of doers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
              <a
                href="#packages"
                data-testid="hero-cta-explore-packages"
                className="inline-flex justify-center items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3.5 text-sm font-medium hover:-translate-y-0.5 transition-transform"
              >
                Explore Packages <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#courses"
                data-testid="hero-cta-view-courses"
                className="inline-flex justify-center items-center gap-2 rounded-full border border-slate-300 dark:border-white/15 bg-white/60 dark:bg-white/5 text-[#0F172A] dark:text-white px-6 py-3.5 text-sm font-medium hover:bg-white dark:hover:bg-white/10 transition-colors"
              >
                <PlayCircle className="h-4 w-4" /> View Courses
              </a>
            </motion.div>

            <div className="mt-10 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex -space-x-2">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="h-7 w-7 rounded-full border-2 border-white dark:border-[#0F172A] bg-gradient-to-br from-[#2563EB] to-[#06B6D4]"
                  />
                ))}
              </div>
              <div>
                <strong className="text-[#0F172A] dark:text-white">
                  12,500+ learners
                </strong>{" "}
                building careers with SkillPreneur Academy.&nbsp;
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-6 relative min-w-0"
          >
            <PackagesPreview />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
